import React, { useEffect, useState, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { MessageCircle, Send, User, Clock, Circle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminChat() {
  const { t } = useLanguage();
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<any>(null);

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv);
  }, [selectedConv]);

  // Poll messages for selected conversation
  useEffect(() => {
    if (selectedConv) {
      pollRef.current = setInterval(() => loadMessages(selectedConv), 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [selectedConv]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/chat/conversations`);
      setConversations(data);
    } catch {}
  };

  const loadMessages = async (convId: string) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/chat/conversations/${convId}/messages`);
      setMessages(data);
    } catch {}
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/admin/chat/conversations/${selectedConv}/reply`, { text: reply });
      setReply('');
      loadMessages(selectedConv);
    } catch {} finally { setSending(false); }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="chat" />
      <main data-testid="admin-chat-page" style={{ flex: 1, background: 'var(--bg-portal)', display: 'flex' }}>
        {/* Conversations list */}
        <div style={{ width: 320, borderRight: '1px solid var(--border)', background: 'white', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageCircle size={20} /> Conversations
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{conversations.length} total</p>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {conversations.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No conversations yet</div>
            ) : conversations.map(c => (
              <div key={c._id} data-testid={`conv-${c._id}`} onClick={() => setSelectedConv(c._id)}
                style={{
                  padding: '14px 20px', borderBottom: '1px solid var(--bg-section)',
                  cursor: 'pointer', transition: 'background 0.15s',
                  background: selectedConv === c._id ? 'rgba(13,71,161,0.06)' : 'white',
                  borderLeft: selectedConv === c._id ? '3px solid var(--primary)' : '3px solid transparent',
                }}
                onMouseEnter={e => { if (selectedConv !== c._id) e.currentTarget.style.background = 'var(--bg-section)'; }}
                onMouseLeave={e => { if (selectedConv !== c._id) e.currentTarget.style.background = 'white'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{c.visitor_name || 'Visitor'}</p>
                  <Circle size={8} fill={c.status === 'active' ? '#16A34A' : '#94A3B8'} style={{ color: c.status === 'active' ? '#16A34A' : '#94A3B8' }} />
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.visitor_email || 'No email'}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                  <Clock size={10} /> {c.updated_at ? new Date(c.updated_at).toLocaleString() : ''}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat messages */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!selectedConv ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              <div style={{ textAlign: 'center' }}>
                <MessageCircle size={48} style={{ opacity: 0.2, marginBottom: 12 }} />
                <p style={{ fontSize: 15 }}>Select a conversation to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'white' }}>
                {(() => { const c = conversations.find(x => x._id === selectedConv); return c ? (
                  <div>
                    <p style={{ fontWeight: 700, fontSize: 15 }}>{c.visitor_name}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.visitor_email} | Started: {c.created_at ? new Date(c.created_at).toLocaleString() : ''}</p>
                  </div>
                ) : null; })()}
              </div>

              {/* Messages area */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg-section)' }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{ alignSelf: msg.from === 'visitor' ? 'flex-start' : 'flex-end', maxWidth: '70%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                      {msg.from === 'visitor' && <User size={10} style={{ color: 'var(--text-muted)' }} />}
                      {msg.from === 'agent' && <User size={10} style={{ color: 'var(--primary)' }} />}
                      <span style={{ fontSize: 10, color: msg.from === 'agent' ? 'var(--primary)' : msg.from === 'bot' ? 'var(--secondary)' : 'var(--text-muted)', fontWeight: 600 }}>
                        {msg.from === 'visitor' ? 'Visitor' : msg.from === 'agent' ? (msg.agent_name || 'Agent') : 'Bot'}
                      </span>
                      <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}</span>
                    </div>
                    <div style={{
                      padding: '10px 14px', fontSize: 14, lineHeight: 1.5,
                      background: msg.from === 'visitor' ? 'white' : msg.from === 'agent' ? 'var(--primary)' : 'rgba(211,47,47,0.08)',
                      color: msg.from === 'agent' ? 'white' : 'var(--text-primary)',
                      border: msg.from === 'visitor' ? '1px solid var(--border)' : 'none',
                      borderRadius: msg.from === 'visitor' ? '12px 12px 12px 0' : '12px 12px 0 12px',
                    }}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply input */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'white', display: 'flex', gap: 8 }}>
                <input data-testid="admin-chat-reply" value={reply} onChange={e => setReply(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleReply()}
                  placeholder="Type your reply..."
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--border)', fontSize: 14, outline: 'none' }} />
                <button data-testid="admin-chat-send" onClick={handleReply} disabled={sending}
                  style={{ padding: '10px 20px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: sending ? 0.5 : 1 }}>
                  <Send size={16} /> Reply
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
